import React from 'react';

interface AnimatedGridProps {
  className?: string;
  variant?: 'dots' | 'lines' | 'matrix';
  animated?: boolean;
}

export const AnimatedGrid: React.FC<AnimatedGridProps> = ({
  className = '',
  variant = 'dots',
  animated = true,
}) => {
  if (variant === 'dots') {
    return (
      <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
        <div
          className="absolute inset-0 opacity-20 dark:opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
        {animated && (
          <div
            className="absolute inset-0 opacity-10 dark:opacity-5 animate-pulse-slow"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, #0A84FF 1px, transparent 0)`,
              backgroundSize: '80px 80px',
            }}
          />
        )}
      </div>
    );
  }

  if (variant === 'lines') {
    return (
      <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
        <svg className="absolute inset-0 w-full h-full opacity-10 dark:opacity-5">
          <defs>
            <pattern
              id="grid-lines"
              width="60"
              height="60"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 60 0 L 0 0 0 60"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
              />
            </pattern>
            {animated && (
              <linearGradient id="grid-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#0A84FF" stopOpacity="0">
                  <animate
                    attributeName="stop-opacity"
                    values="0;0.5;0"
                    dur="3s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="50%" stopColor="#5856D6" stopOpacity="0.3">
                  <animate
                    attributeName="stop-opacity"
                    values="0.3;0.6;0.3"
                    dur="3s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="100%" stopColor="#0A84FF" stopOpacity="0">
                  <animate
                    attributeName="stop-opacity"
                    values="0;0.5;0"
                    dur="3s"
                    repeatCount="indefinite"
                  />
                </stop>
              </linearGradient>
            )}
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-lines)" />
          {animated && (
            <rect
              width="100%"
              height="100%"
              fill="url(#grid-gradient)"
              style={{ mixBlendMode: 'overlay' }}
            />
          )}
        </svg>
      </div>
    );
  }

  // Matrix variant - tech/cyberpunk style
  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      <div className="absolute inset-0 opacity-5 dark:opacity-10">
        <svg className="w-full h-full">
          <defs>
            <pattern
              id="matrix-grid"
              width="30"
              height="30"
              patternUnits="userSpaceOnUse"
            >
              <rect width="30" height="30" fill="none" />
              <circle cx="15" cy="15" r="0.5" fill="#0A84FF" />
              <path
                d="M 0 15 H 30 M 15 0 V 30"
                stroke="#0A84FF"
                strokeWidth="0.25"
                opacity="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#matrix-grid)" />
        </svg>
      </div>

      {animated && (
        <>
          {/* Scanning line effect */}
          <div
            className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-light dark:via-primary-dark to-transparent opacity-50"
            style={{
              animation: 'scan 4s linear infinite',
            }}
          />
          <style>{`
            @keyframes scan {
              0% { top: 0; }
              100% { top: 100%; }
            }
          `}</style>

          {/* Corner accents */}
          <div className="absolute top-4 left-4 w-16 h-16 border-l-2 border-t-2 border-primary-light/30 dark:border-primary-dark/30 rounded-tl-lg" />
          <div className="absolute top-4 right-4 w-16 h-16 border-r-2 border-t-2 border-primary-light/30 dark:border-primary-dark/30 rounded-tr-lg" />
          <div className="absolute bottom-4 left-4 w-16 h-16 border-l-2 border-b-2 border-primary-light/30 dark:border-primary-dark/30 rounded-bl-lg" />
          <div className="absolute bottom-4 right-4 w-16 h-16 border-r-2 border-b-2 border-primary-light/30 dark:border-primary-dark/30 rounded-br-lg" />
        </>
      )}
    </div>
  );
};

export default AnimatedGrid;
